import { Activity, RefreshCcw } from 'lucide-react';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  EmptyState,
  ErrorState,
  Spinner,
} from '@helios/ui';
import {
  realtimeErrorKind,
  type RealtimeEventItem,
  type RiskLevel,
} from '@/services/realtimeClient';

interface RealtimeEventFeedProps {
  events: RealtimeEventItem[] | undefined;
  isLoading: boolean;
  isError: boolean;
  error?: unknown;
  socketStatus: 'connecting' | 'connected' | 'disconnected' | 'error';
  onRetry: () => void;
}

export function RealtimeEventFeed({
  events,
  isLoading,
  isError,
  error,
  socketStatus,
  onRetry,
}: RealtimeEventFeedProps) {
  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex min-h-[220px] items-center justify-center">
          <div className="flex items-center gap-3 text-sm text-muted-foreground">
            <Spinner className="h-4 w-4 text-primary" aria-hidden="true" />
            Loading realtime feed…
          </div>
        </CardContent>
      </Card>
    );
  }

  if (isError) {
    const kind = realtimeErrorKind(error);
    const title =
      kind === 'permission'
        ? 'Realtime feed access restricted'
        : kind === 'offline'
          ? 'Realtime feed offline'
          : 'Realtime feed unavailable';
    const description =
      kind === 'permission'
        ? 'Your current role cannot read the tenant event replay.'
        : kind === 'offline'
          ? 'The API is unreachable. Existing dashboard data remains available while the connection recovers.'
          : 'The event replay endpoint did not return recent tenant events.';

    return (
      <ErrorState
        title={title}
        description={description}
        action={
          <Button type="button" variant="outline" size="sm" onClick={onRetry}>
            <RefreshCcw className="h-4 w-4" aria-hidden="true" />
            Retry
          </Button>
        }
      />
    );
  }

  const visibleEvents = Array.isArray(events) ? events : [];

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between space-y-0">
        <CardTitle className="flex items-center gap-2 text-base">
          <Activity className="h-4 w-4 text-primary" aria-hidden="true" />
          Live Event Stream
        </CardTitle>
        <Badge variant={socketStatus === 'connected' ? 'success' : socketStatus === 'error' ? 'critical' : 'warning'}>
          {socketLabel(socketStatus)}
        </Badge>
      </CardHeader>
      <CardContent>
        {visibleEvents.length === 0 ? (
          <EmptyState
            title="No realtime events"
            description="S05 stores and replays tenant events here as dashboard, alert, and project activity occurs."
          />
        ) : (
          <ul className="divide-y divide-border overflow-hidden rounded-lg border border-border">
            {visibleEvents.slice(0, 8).map((event) => (
              <li key={event.id} className="px-3 py-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge variant={severityVariant(event.severity)}>{event.severity ?? 'INFO'}</Badge>
                      <span className="text-xs text-muted-foreground">{formatTime(event.timestamp)}</span>
                    </div>
                    <p className="mt-2 truncate text-sm font-medium text-foreground">{event.eventName}</p>
                    <p className="mt-1 text-xs text-muted-foreground">
                      {event.entityType}{event.entityId ? ` · ${event.entityId}` : ''} · {event.topic}
                    </p>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

function socketLabel(status: RealtimeEventFeedProps['socketStatus']): string {
  if (status === 'connected') return 'Socket live';
  if (status === 'connecting') return 'Connecting';
  if (status === 'error') return 'Socket error';
  return 'Disconnected';
}

function severityVariant(severity: RiskLevel | null) {
  if (severity === 'CRITICAL') return 'critical';
  if (severity === 'HIGH') return 'warning';
  if (severity === 'MODERATE') return 'info';
  return 'success';
}

function formatTime(value: string): string {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
}
